<template>
    <div class="mt-3" v-if="hasParticipants">
        <h5 class="mb-2"><i class="fas fa-users"></i> Участники процесса:</h5>
        <p class="small text-muted mb-2">Нажмите на ФИО, чтобы посмотреть контакты.</p>
        <div class="participants-grid">
            <!-- Инициатор -->
            <div class="participant-column" v-if="initiator">
                <div class="participant-section">
                    <div class="participant-role-heading">
                        <div class="participant-role-title">
                            <i class="fas fa-user-edit"></i> Инициатор
                        </div>
                        <b-button
                            v-if="canReplaceParticipant(initiator)"
                            variant="outline-primary"
                            size="sm"
                            class="participant-action-btn"
                            @click="openReplaceParticipant(initiator)"
                        >
                            <i class="fas fa-exchange-alt"></i> Заменить
                        </b-button>
                    </div>
                    <div class="participant-item">
                        <ParticipantStatusBadge
                            :text="getParticipantDisplayStatusText(initiator, 'created')"
                            :variant="getParticipantDisplayStatusVariant(initiator, 'created')"
                            :decision="initiator.decision"
                        />
                        <TelegramUserLink
                            class="participant-name"
                            :user="initiatorUser"
                            :fallback="initiatorName"
                            name-order="last-first"
                        />
                    </div>
                </div>
            </div>

            <!-- Согласующие (скрываем, если заявка автосогласована единственным согласующим — инициатором) -->
            <div class="participant-column" v-if="showApproversBlock">
                <div class="participant-section">
                    <div class="participant-role-heading">
                        <div class="participant-role-title">
                            <i class="fas fa-user-check"></i> Согласующие
                        </div>
                        <b-button
                            v-if="canAddApproverAction"
                            variant="outline-success"
                            size="sm"
                            class="participant-action-btn"
                            @click="openAddApproverAction"
                        >
                            <i class="fas fa-user-plus"></i> Добавить
                        </b-button>
                    </div>
                    <div v-if="!approvers.length" class="participant-empty">
                        Согласующих пока нет
                    </div>
                    <div v-for="approver in approvers" :key="approver.id" class="participant-item">
                        <ParticipantStatusBadge
                            :text="getParticipantDisplayStatusText(approver)"
                            :variant="getParticipantDisplayStatusVariant(approver)"
                            :decision="approver.decision"
                        />
                        <TelegramUserLink class="participant-name" :user="approver.User" name-order="last-first" />
                        <div class="participant-actions" v-if="canReplaceApproverAction(approver) || canRemoveApproverAction(approver)">
                            <b-button
                                v-if="canReplaceApproverAction(approver)"
                                variant="outline-primary"
                                size="sm"
                                class="participant-action-btn"
                                v-b-tooltip.hover
                                title="Заменить"
                                @click="openReplaceApproverAction(approver)"
                            >
                                <i class="fas fa-exchange-alt"></i> Заменить
                            </b-button>
                            <b-button
                                v-if="canRemoveApproverAction(approver)"
                                variant="outline-danger"
                                size="sm"
                                class="participant-action-btn"
                                v-b-tooltip.hover
                                title="Удалить"
                                @click="openRemoveApproverAction(approver)"
                            >
                                <i class="fas fa-user-minus"></i> Удалить
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Назначающие исполнителя -->
            <div class="participant-column" v-if="assigners.length || canAddParticipantRole('assigner')">
                <div class="participant-section">
                    <div class="participant-role-heading">
                        <div class="participant-role-title">
                            <i class="fas fa-user-plus"></i> Назначающие
                        </div>
                        <b-button
                            v-if="canAddParticipantRole('assigner')"
                            variant="outline-success"
                            size="sm"
                            class="participant-action-btn"
                            @click="openAddParticipant('assigner')"
                        >
                            <i class="fas fa-user-plus"></i> Добавить
                        </b-button>
                    </div>
                    <div v-if="!assigners.length" class="participant-empty">
                        Назначающих пока нет
                    </div>
                    <div v-for="assigner in assigners" :key="assigner.id" class="participant-item">
                        <ParticipantStatusBadge
                            :text="getParticipantDisplayStatusText(assigner, null, getAssignerStatusText(assigner))"
                            :variant="getParticipantDisplayStatusVariant(assigner, null, getAssignerStatusVariant(assigner))"
                            :decision="assigner.decision"
                        />
                        <TelegramUserLink class="participant-name" :user="assigner.User" name-order="last-first" />
                        <div class="participant-actions" v-if="canReplaceParticipant(assigner) || canRemoveParticipant(assigner)">
                            <b-button
                                v-if="canReplaceParticipant(assigner)"
                                variant="outline-primary"
                                size="sm"
                                class="participant-action-btn"
                                v-b-tooltip.hover
                                title="Заменить"
                                @click="openReplaceParticipant(assigner)"
                            >
                                <i class="fas fa-exchange-alt"></i> Заменить
                            </b-button>
                            <b-button
                                v-if="canRemoveParticipant(assigner)"
                                variant="outline-danger"
                                size="sm"
                                class="participant-action-btn"
                                v-b-tooltip.hover
                                title="Удалить"
                                @click="openRemoveParticipant(assigner)"
                            >
                                <i class="fas fa-user-minus"></i> Удалить
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Исполнители -->
            <div class="participant-column" v-if="executors.length || canAddParticipantRole('executor')">
                <div class="participant-section">
                    <div class="participant-role-heading">
                        <div class="participant-role-title">
                            <i class="fas fa-hammer"></i> Исполнители
                        </div>
                        <b-button
                            v-if="canAddParticipantRole('executor')"
                            variant="outline-success"
                            size="sm"
                            class="participant-action-btn"
                            @click="openAddParticipant('executor')"
                        >
                            <i class="fas fa-user-plus"></i> Добавить
                        </b-button>
                    </div>
                    <div v-if="!executors.length" class="participant-empty">
                        Исполнителей пока нет
                    </div>
                    <div v-for="executor in executors" :key="executor.id" class="participant-item">
                        <ParticipantStatusBadge
                            :text="getParticipantDisplayStatusText(executor)"
                            :variant="getParticipantDisplayStatusVariant(executor)"
                            :decision="executor.decision"
                        />
                        <TelegramUserLink class="participant-name" :user="executor.User" name-order="last-first" />
                        <div class="participant-actions" v-if="canReplaceParticipant(executor) || canRemoveParticipant(executor)">
                            <b-button
                                v-if="canReplaceParticipant(executor)"
                                variant="outline-primary"
                                size="sm"
                                class="participant-action-btn"
                                v-b-tooltip.hover
                                title="Заменить"
                                @click="openReplaceParticipant(executor)"
                            >
                                <i class="fas fa-exchange-alt"></i> Заменить
                            </b-button>
                            <b-button
                                v-if="canRemoveParticipant(executor)"
                                variant="outline-danger"
                                size="sm"
                                class="participant-action-btn"
                                v-b-tooltip.hover
                                title="Удалить"
                                @click="openRemoveParticipant(executor)"
                            >
                                <i class="fas fa-user-minus"></i> Удалить
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Наблюдатели -->
            <div class="participant-column" v-if="observers.length || canAddParticipantRole('observer')">
                <div class="participant-section">
                    <div class="participant-role-heading">
                        <div class="participant-role-title">
                            <i class="fas fa-eye"></i> Наблюдатели
                        </div>
                        <b-button
                            v-if="canAddParticipantRole('observer')"
                            variant="outline-success"
                            size="sm"
                            class="participant-action-btn"
                            @click="openAddParticipant('observer')"
                        >
                            <i class="fas fa-user-plus"></i> Добавить
                        </b-button>
                    </div>
                    <div v-if="!observers.length" class="participant-empty">
                        Наблюдателей пока нет
                    </div>
                    <div v-for="observer in observers" :key="observer.id" class="participant-item">
                        <ParticipantStatusBadge
                            :text="getParticipantDisplayStatusText(observer, null, 'Наблюдает')"
                            :variant="getParticipantDisplayStatusVariant(observer, null, 'light')"
                            :decision="observer.decision"
                            class="observer-badge"
                        />
                        <TelegramUserLink class="participant-name" :user="observer.User" name-order="last-first" />
                        <div class="participant-actions" v-if="canReplaceParticipant(observer) || canRemoveParticipant(observer)">
                            <b-button
                                v-if="canReplaceParticipant(observer)"
                                variant="outline-primary"
                                size="sm"
                                class="participant-action-btn"
                                v-b-tooltip.hover
                                title="Заменить"
                                @click="openReplaceParticipant(observer)"
                            >
                                <i class="fas fa-exchange-alt"></i> Заменить
                            </b-button>
                            <b-button
                                v-if="canRemoveParticipant(observer)"
                                variant="outline-danger"
                                size="sm"
                                class="participant-action-btn"
                                v-b-tooltip.hover
                                title="Удалить"
                                @click="openRemoveParticipant(observer)"
                            >
                                <i class="fas fa-user-minus"></i> Удалить
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <b-modal content-class="argus-glass-dialog" ignore-enforce-focus-selector=".request-employee-panel"
            v-model="replaceModalVisible"
            title="Заменить согласующего"
            ok-title="Заменить"
            cancel-title="Отмена"
            :ok-disabled="isReplaceSubmitDisabled"
            @ok="submitReplaceApprover"
            @hidden="resetApproverModalState"
        >
            <div v-if="selectedApprover">
                <p class="mb-2">
                    Текущий согласующий:
                    <strong>{{ formatParticipantName(selectedApprover) }}</strong>
                </p>

                <b-alert variant="danger" show v-if="approverActionError">
                    {{ approverActionError }}
                </b-alert>

                <b-form-group label="Новый согласующий:" label-for="approver-replacement-user">
                    <v-select
                        id="approver-replacement-user"
                        v-model="selectedReplacementUserId"
                        :options="replacementSearchOptions"
                        label="text"
                        :reduce="option => option.value"
                        :disabled="replacementOptionsLoading || replacementLoading"
                        :loading="replacementOptionsLoading"
                        placeholder="Начните вводить имя согласующего..."
                        :clearable="true"
                        append-to-body
                        :calculate-position="positionModalSelectDropdown"
                    >
                        <template #no-options>
                            Сотрудники не найдены
                        </template>
                    </v-select>
                    <EmployeeSelectionPreview :user-id="selectedReplacementUserId" :options="replacementSearchOptions" />
                    <small class="text-muted">
                        {{ replacementOptionsHelpText }}
                    </small>
                </b-form-group>

                <b-alert variant="warning" show v-if="!replacementOptionsLoading && replacementOptions.length === 0">
                    Подходящих сотрудников для замены не найдено.
                </b-alert>

                <b-form-group :label="reasonFieldLabel" label-for="approver-replacement-reason">
                    <DiscussionEditor
                        id="approver-replacement-reason"
                        v-model="approverActionReason"
                        placeholder="Например: отпуск, больничный, командировка"
                        :disabled="replacementLoading"
                    />
                    <small class="text-muted" v-if="isApproverReasonRequired">
                        Для не-администраторов причина обязательна.
                    </small>
                </b-form-group>

                <b-form-checkbox
                    v-if="isCurrentUserAdmin"
                    v-model="suppressApproverNotification"
                    :disabled="replacementLoading"
                >
                    Не уведомлять о смене состава согласующих
                </b-form-checkbox>
            </div>
        </b-modal>

        <b-modal content-class="argus-glass-dialog" ignore-enforce-focus-selector=".request-employee-panel"
            v-model="addModalVisible"
            title="Добавить согласующего"
            ok-title="Добавить"
            cancel-title="Отмена"
            :ok-disabled="isAddSubmitDisabled"
            @ok="submitAddApprover"
            @hidden="resetApproverModalState"
        >
            <div>
                <b-alert variant="danger" show v-if="approverActionError">
                    {{ approverActionError }}
                </b-alert>

                <b-form-group label="Новый согласующий:" label-for="approver-add-user">
                    <v-select
                        id="approver-add-user"
                        v-model="selectedAddApproverUserId"
                        :options="addApproverSearchOptions"
                        label="text"
                        :reduce="option => option.value"
                        :disabled="addOptionsLoading || replacementLoading"
                        :loading="addOptionsLoading"
                        placeholder="Начните вводить имя согласующего..."
                        :clearable="true"
                        append-to-body
                        :calculate-position="positionModalSelectDropdown"
                    >
                        <template #no-options>
                            Сотрудники не найдены
                        </template>
                    </v-select>
                    <EmployeeSelectionPreview :user-id="selectedAddApproverUserId" :options="addApproverSearchOptions" />
                    <small class="text-muted">
                        В списке активные сотрудники, которых еще нет в маршруте заявки.
                    </small>
                </b-form-group>

                <b-alert variant="warning" show v-if="!addOptionsLoading && addOptions.length === 0">
                    Подходящих сотрудников для добавления не найдено.
                </b-alert>

                <b-form-group :label="reasonFieldLabel" label-for="approver-add-reason">
                    <DiscussionEditor
                        id="approver-add-reason"
                        v-model="approverActionReason"
                        placeholder="Например: согласующий отсутствует, нужен дополнительный руководитель"
                        :disabled="replacementLoading"
                    />
                    <small class="text-muted" v-if="isApproverReasonRequired">
                        Для не-администраторов причина обязательна.
                    </small>
                </b-form-group>

                <b-form-checkbox
                    v-if="isCurrentUserAdmin"
                    v-model="suppressApproverNotification"
                    :disabled="replacementLoading"
                >
                    Не уведомлять о смене состава согласующих
                </b-form-checkbox>
            </div>
        </b-modal>

        <b-modal content-class="argus-glass-dialog" ignore-enforce-focus-selector=".request-employee-panel"
            v-model="removeModalVisible"
            title="Удалить согласующего"
            ok-title="Удалить"
            ok-variant="danger"
            cancel-title="Отмена"
            :ok-disabled="isRemoveSubmitDisabled"
            @ok="submitRemoveApprover"
            @hidden="resetApproverModalState"
        >
            <div v-if="selectedApprover">
                <b-alert variant="warning" show>
                    Согласующий <strong>{{ formatParticipantName(selectedApprover) }}</strong> будет удален из маршрута этой заявки.
                    Если ожидающих согласующих не останется, заявка перейдет на следующий этап.
                </b-alert>

                <b-alert variant="danger" show v-if="approverActionError">
                    {{ approverActionError }}
                </b-alert>

                <b-form-group :label="reasonFieldLabel" label-for="approver-remove-reason">
                    <DiscussionEditor
                        id="approver-remove-reason"
                        v-model="approverActionReason"
                        placeholder="Например: согласующий отсутствует, согласование не требуется"
                        :disabled="replacementLoading"
                    />
                    <small class="text-muted" v-if="isApproverReasonRequired">
                        Для не-администраторов причина обязательна.
                    </small>
                </b-form-group>

                <b-form-checkbox
                    v-if="isCurrentUserAdmin"
                    v-model="suppressApproverNotification"
                    :disabled="replacementLoading"
                >
                    Не уведомлять о смене состава согласующих
                </b-form-checkbox>
            </div>
        </b-modal>

        <b-modal content-class="argus-glass-dialog" ignore-enforce-focus-selector=".request-employee-panel"
            v-model="participantModalVisible"
            :title="participantModalTitle"
            :ok-title="participantModalOkTitle"
            :ok-variant="participantModalOkVariant"
            cancel-title="Отмена"
            :ok-disabled="isParticipantSubmitDisabled"
            @ok="submitParticipantAction"
            @hidden="resetParticipantModalState"
        >
            <div>
                <b-alert variant="danger" show v-if="participantActionError">
                    {{ participantActionError }}
                </b-alert>

                <b-alert variant="warning" show v-if="participantModalMode === 'remove' && selectedParticipant">
                    {{ participantRemoveWarningText }}
                </b-alert>

                <template v-if="participantModalMode !== 'remove'">
                    <p class="mb-2" v-if="participantModalMode === 'replace' && selectedParticipant">
                        Текущий участник:
                        <strong>{{ formatParticipantName(selectedParticipant) }}</strong>
                    </p>

                    <b-form-group :label="participantUserFieldLabel" label-for="participant-action-user">
                        <v-select
                            id="participant-action-user"
                            v-model="selectedParticipantUserId"
                            :options="participantSearchOptions"
                            label="text"
                            :reduce="option => option.value"
                            :disabled="participantOptionsLoading || participantActionLoading"
                            :loading="participantOptionsLoading"
                            :placeholder="participantUserPlaceholder"
                            :clearable="true"
                            append-to-body
                            :calculate-position="positionModalSelectDropdown"
                        >
                            <template #no-options>
                                Сотрудники не найдены
                            </template>
                        </v-select>
                        <EmployeeSelectionPreview :user-id="selectedParticipantUserId" :options="participantSearchOptions" />
                        <small class="text-muted">
                            В списке активные сотрудники, которых еще нет в этой роли в заявке.
                        </small>
                    </b-form-group>

                    <b-alert
                        variant="warning"
                        show
                        v-if="!participantOptionsLoading && participantOptions.length === 0"
                    >
                        Подходящих сотрудников не найдено.
                    </b-alert>
                </template>

                <b-form-group :label="participantReasonFieldLabel" label-for="participant-action-reason">
                    <DiscussionEditor
                        id="participant-action-reason"
                        v-model="participantActionReason"
                        placeholder="Например: кадровая замена, уточнение маршрута, ручная корректировка"
                        :disabled="participantActionLoading"
                    />
                    <small class="text-muted" v-if="isParticipantReasonRequired">
                        Для не-администраторов причина обязательна.
                    </small>
                </b-form-group>
            </div>
        </b-modal>
    </div>
</template>

<script>
import EmployeeSelectionPreview from '@/components/EmployeeSelectionPreview.vue';
import { 
    getParticipantStatusText, 
    getParticipantStatusVariant,
    getAssignerStatusText,
    getAssignerStatusVariant,
    getInitiatorDisplayName,
    getParticipantRoleText
} from '@/utils/requestCard/formatters';
import { positionVueSelectDropdown } from '@/utils/vueSelectPositioning';
import TelegramUserLink from './TelegramUserLink.vue';
import ParticipantStatusBadge from './ParticipantStatusBadge.vue';

/**
 * Компонент отображения участников процесса заявки
 */
export default {
    name: 'RequestParticipants',
    components: {
        EmployeeSelectionPreview,
        ParticipantStatusBadge,
        TelegramUserLink
    },
    props: {
        readOnly: {
            type: Boolean,
            default: false
        },
        initiator: {
            type: Object,
            default: null
        },
        approvers: {
            type: Array,
            default: () => []
        },
        assigners: {
            type: Array,
            default: () => []
        },
        executors: {
            type: Array,
            default: () => []
        },
        observers: {
            type: Array,
            default: () => []
        },
        request: {
            type: Object,
            default: () => ({})
        },
        adminGodModeEnabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            replaceModalVisible: false,
            addModalVisible: false,
            removeModalVisible: false,
            selectedApprover: null,
            selectedReplacementUserId: null,
            selectedAddApproverUserId: null,
            replacementOptions: [],
            addOptions: [],
            replacementOptionsLoading: false,
            addOptionsLoading: false,
            replacementLoading: false,
            approverActionReason: '',
            approverActionError: null,
            suppressApproverNotification: false,
            participantModalVisible: false,
            participantModalMode: null,
            selectedParticipant: null,
            selectedParticipantRole: null,
            selectedParticipantUserId: null,
            participantOptions: [],
            participantOptionsLoading: false,
            participantActionLoading: false,
            participantActionReason: '',
            participantActionError: null
        };
    },
    computed: {
        /** Скрыть блок «Согласующие», если заявка уже согласована и единственный согласующий — инициатор (автосогласование). */
        showApproversBlock() {
            if (!this.approvers.length) return this.canAddApproverAction;
            const status = this.request && this.request.status;
            const pastApprovalStatuses = ['awaiting_executor', 'in_progress', 'awaiting_acceptance', 'completed'];
            if (!status || !pastApprovalStatuses.includes(status)) return true;
            const uniqueApproverIds = [...new Set(this.approvers.map(a => a.user_id))];
            if (uniqueApproverIds.length !== 1) return true;
            return uniqueApproverIds[0] !== (this.request && this.request.initiator_id);
        },
        hasParticipants() {
            return this.initiator ||
                this.showApproversBlock ||
                this.assigners.length > 0 ||
                this.executors.length > 0 ||
                this.observers.length > 0 ||
                this.canAddParticipantRole('assigner') ||
                this.canAddParticipantRole('executor') ||
                this.canAddParticipantRole('observer');
        },
        initiatorName() {
            return getInitiatorDisplayName(this.initiator, this.request);
        },
        initiatorUser() {
            if (this.initiator && this.initiator.User) {
                return this.initiator.User;
            }

            return this.request && this.request.Initiator ? this.request.Initiator : null;
        },
        canReplaceApprover() {
            return !this.readOnly && this.request && this.request.permissions && this.request.permissions.canReplaceApprover === true;
        },
        canRemoveApprover() {
            return !this.readOnly && this.request && this.request.permissions && this.request.permissions.canRemoveApprover === true;
        },
        canAddApprover() {
            return !this.readOnly && this.request && this.request.permissions && this.request.permissions.canAddApprover === true;
        },
        canReplaceApproverWithAnyEmployee() {
            return this.request &&
                this.request.permissions &&
                this.request.permissions.canReplaceApproverWithAnyEmployee === true;
        },
        isCurrentUserAdmin() {
            return this.request && this.request.isAdmin === true && this.adminGodModeEnabled === true;
        },
        isApproverReasonRequired() {
            return !this.isCurrentUserAdmin;
        },
        hasApproverActionReason() {
            return String(this.approverActionReason || '').trim().length > 0;
        },
        reasonFieldLabel() {
            return this.isApproverReasonRequired ? 'Причина *:' : 'Причина:';
        },
        isReplaceSubmitDisabled() {
            return !this.selectedReplacementUserId ||
                this.replacementLoading ||
                this.replacementOptionsLoading ||
                (this.isApproverReasonRequired && !this.hasApproverActionReason);
        },
        isRemoveSubmitDisabled() {
            return this.replacementLoading ||
                (this.isApproverReasonRequired && !this.hasApproverActionReason);
        },
        isAddSubmitDisabled() {
            return !this.selectedAddApproverUserId ||
                this.replacementLoading ||
                this.addOptionsLoading ||
                (this.isApproverReasonRequired && !this.hasApproverActionReason);
        },
        replacementOptionsHelpText() {
            if (this.canReplaceApproverWithAnyEmployee) {
                return 'В списке активные сотрудники, которых еще нет в маршруте заявки.';
            }

            return 'В списке только активные сотрудники из того же отдела, которых еще нет в маршруте заявки.';
        },
        replacementSearchOptions() {
            return this.replacementOptions.map(user => ({
                value: user.user_id,
                text: this.formatReplacementUserOption(user)
            }));
        },
        addApproverSearchOptions() {
            return this.addOptions.map(user => ({
                value: user.user_id,
                text: this.formatReplacementUserOption(user)
            }));
        },
        canManageRequestParticipants() {
            return !this.readOnly && this.request &&
                this.request.permissions &&
                this.request.permissions.canManageRequestParticipants === true;
        },
        canAddRequestParticipant() {
            return !this.readOnly && this.request &&
                this.request.permissions &&
                this.request.permissions.canAddRequestParticipant === true;
        },
        canReplaceRequestParticipant() {
            return !this.readOnly && this.request &&
                this.request.permissions &&
                this.request.permissions.canReplaceRequestParticipant === true;
        },
        canRemoveRequestParticipant() {
            return !this.readOnly && this.request &&
                this.request.permissions &&
                this.request.permissions.canRemoveRequestParticipant === true;
        },
        canUseAdminParticipantManagement() {
            return this.canManageRequestParticipants;
        },
        canShowClassicApproverManagement() {
            return true;
        },
        canAddApproverAction() {
            return (this.canAddApprover && this.canShowClassicApproverManagement) ||
                this.canAddParticipantRole('approver');
        },
        participantRoleLabel() {
            return getParticipantRoleText(this.selectedParticipantRole) || 'Участник';
        },
        participantModalTitle() {
            if (this.participantModalMode === 'add') {
                return `Добавить: ${this.participantRoleLabel}`;
            }
            if (this.participantModalMode === 'remove') {
                return `Удалить: ${this.participantRoleLabel}`;
            }
            return `Заменить: ${this.participantRoleLabel}`;
        },
        participantModalOkTitle() {
            if (this.participantModalMode === 'add') return 'Добавить';
            if (this.participantModalMode === 'remove') return 'Удалить';
            return 'Заменить';
        },
        participantModalOkVariant() {
            return this.participantModalMode === 'remove' ? 'danger' : 'primary';
        },
        participantUserFieldLabel() {
            return `Новый ${String(this.participantRoleLabel || 'участник').toLowerCase()}:`;
        },
        participantUserPlaceholder() {
            return `Начните вводить имя ${String(this.participantRoleLabel || 'участника').toLowerCase()}...`;
        },
        participantRemoveWarningText() {
            const role = this.participantRoleLabel || 'Участник';
            const name = this.formatParticipantName(this.selectedParticipant);
            return `${role} ${name} будет удален из участников этой заявки.`;
        },
        isParticipantReasonRequired() {
            return !this.isCurrentUserAdmin;
        },
        hasParticipantActionReason() {
            return String(this.participantActionReason || '').trim().length > 0;
        },
        participantReasonFieldLabel() {
            return this.isParticipantReasonRequired ? 'Причина *:' : 'Причина:';
        },
        isParticipantSubmitDisabled() {
            if (this.participantActionLoading || this.participantOptionsLoading) {
                return true;
            }
            if (this.isParticipantReasonRequired && !this.hasParticipantActionReason) {
                return true;
            }
            if (this.participantModalMode === 'remove') {
                return !this.selectedParticipant;
            }
            return !this.selectedParticipantUserId;
        },
        participantSearchOptions() {
            return this.participantOptions.map(user => ({
                value: user.user_id,
                text: this.formatReplacementUserOption(user)
            }));
        }
    },
    methods: {
        getParticipantDisplayStatus(participant, fallbackStatus = null) {
            if (participant && participant.decision && participant.decision.badge_status) {
                return participant.decision.badge_status;
            }
            return fallbackStatus || (participant && participant.status) || 'pending';
        },
        getParticipantDisplayStatusText(participant, fallbackStatus = null, fallbackText = null) {
            if (participant && participant.decision && participant.decision.badge_status) {
                return getParticipantStatusText(participant.decision.badge_status);
            }
            return fallbackText || getParticipantStatusText(this.getParticipantDisplayStatus(participant, fallbackStatus));
        },
        getParticipantDisplayStatusVariant(participant, fallbackStatus = null, fallbackVariant = null) {
            if (participant && participant.decision && participant.decision.badge_status) {
                return getParticipantStatusVariant(participant.decision.badge_status);
            }
            return fallbackVariant || getParticipantStatusVariant(this.getParticipantDisplayStatus(participant, fallbackStatus));
        },
        getParticipantStatusText,
        getParticipantStatusVariant,
        getAssignerStatusText(assigner) {
            return getAssignerStatusText(assigner, this.executors);
        },
        getAssignerStatusVariant(assigner) {
            return getAssignerStatusVariant(assigner, this.executors);
        },
        getParticipantRoleText,
        positionModalSelectDropdown: positionVueSelectDropdown,
        permissionRoleList(key) {
            const permissions = this.request && this.request.permissions ? this.request.permissions : {};
            return Array.isArray(permissions[key]) ? permissions[key] : [];
        },
        hasRolePermission(key, role) {
            return this.permissionRoleList(key).includes(role);
        },
        canAddParticipantRole(role) {
            if (!this.canAddRequestParticipant || role === 'initiator') {
                return false;
            }
            if (role === 'approver') {
                return this.isCurrentUserAdmin;
            }
            return this.hasRolePermission('canAddRequestParticipantRoles', role);
        },
        canReplaceParticipant(participant) {
            if (!this.canReplaceRequestParticipant || !participant || !participant.id || !participant.role) {
                return false;
            }
            if (participant.role === 'approver') {
                return this.isCurrentUserAdmin;
            }
            if (!this.hasRolePermission('canReplaceRequestParticipantRoles', participant.role)) {
                return false;
            }
            if (participant.role === 'executor' && ['completed', 'partially_completed'].includes(participant.status)) {
                return false;
            }
            return true;
        },
        canRemoveParticipant(participant) {
            if (!this.canRemoveRequestParticipant || !participant || !participant.id || !participant.role) {
                return false;
            }
            if (participant.role === 'initiator') {
                return false;
            }
            if (participant.role === 'approver') {
                return this.isCurrentUserAdmin;
            }
            if (!this.hasRolePermission('canRemoveRequestParticipantRoles', participant.role)) {
                return false;
            }
            if (participant.role === 'executor' && ['active', 'completed', 'partially_completed'].includes(participant.status)) {
                return false;
            }
            return true;
        },
        canManageApprover(approver) {
            return approver &&
                approver.status === 'pending' &&
                (this.canReplaceApprover || this.canRemoveApprover);
        },
        canReplaceApproverAction(approver) {
            return this.canUseClassicApproverManagement(approver, 'replace') ||
                this.canReplaceParticipant(approver);
        },
        canRemoveApproverAction(approver) {
            return this.canUseClassicApproverManagement(approver, 'remove') ||
                this.canRemoveParticipant(approver);
        },
        formatParticipantName(participant) {
            if (!participant) return '';
            if (!participant.user_id) return 'пустой слот';
            const user = participant.User || {};
            const name = `${user.last_name || ''} ${user.first_name || ''}`.trim();
            return name || `Пользователь #${participant.user_id}`;
        },
        formatReplacementUserOption(user) {
            const name = `${user.last_name || ''} ${user.first_name || ''}`.trim() || `Пользователь #${user.user_id}`;
            const departments = Array.isArray(user.department_names) && user.department_names.length
                ? ` (${user.department_names.join(', ')})`
                : '';
            return `${name}${departments}`;
        },
        async openReplaceApprover(approver) {
            this.selectedApprover = approver;
            this.selectedReplacementUserId = null;
            this.selectedAddApproverUserId = null;
            this.replacementOptions = [];
            this.addOptions = [];
            this.approverActionReason = '';
            this.approverActionError = null;
            this.suppressApproverNotification = false;
            this.replaceModalVisible = true;
            this.replacementOptionsLoading = true;

            try {
                const result = await this.$store.dispatch(
                    'requestCard/requestCard_getApproverReplacementOptions',
                    {
                        participantId: approver.id,
                        godMode: this.adminGodModeEnabled === true
                    }
                );
                this.replacementOptions = (result && result.users) || [];
            } catch (error) {
                this.approverActionError = this.getErrorMessage(error);
            } finally {
                this.replacementOptionsLoading = false;
            }
        },
        openRemoveApprover(approver) {
            this.selectedApprover = approver;
            this.selectedReplacementUserId = null;
            this.selectedAddApproverUserId = null;
            this.replacementOptions = [];
            this.addOptions = [];
            this.approverActionReason = '';
            this.approverActionError = null;
            this.suppressApproverNotification = false;
            this.removeModalVisible = true;
        },
        async openAddApprover() {
            this.selectedApprover = null;
            this.selectedReplacementUserId = null;
            this.selectedAddApproverUserId = null;
            this.replacementOptions = [];
            this.addOptions = [];
            this.approverActionReason = '';
            this.approverActionError = null;
            this.suppressApproverNotification = false;
            this.addModalVisible = true;
            this.addOptionsLoading = true;

            try {
                const result = await this.$store.dispatch('requestCard/requestCard_getApproverAddOptions', {
                    godMode: this.adminGodModeEnabled === true
                });
                this.addOptions = (result && result.users) || [];
            } catch (error) {
                this.approverActionError = this.getErrorMessage(error);
            } finally {
                this.addOptionsLoading = false;
            }
        },
        canUseClassicApproverManagement(approver, action) {
            if (!this.canShowClassicApproverManagement) {
                return false;
            }
            if (!approver || approver.role !== 'approver' || approver.status !== 'pending') {
                return false;
            }
            if (action === 'replace') {
                return this.canReplaceApprover === true;
            }
            if (action === 'remove') {
                return this.canRemoveApprover === true;
            }
            return false;
        },
        openReplaceApproverAction(approver) {
            if (this.canUseClassicApproverManagement(approver, 'replace')) {
                this.openReplaceApprover(approver);
                return;
            }
            this.openReplaceParticipant(approver);
        },
        openRemoveApproverAction(approver) {
            if (this.canUseClassicApproverManagement(approver, 'remove')) {
                this.openRemoveApprover(approver);
                return;
            }
            this.openRemoveParticipant(approver);
        },
        openAddApproverAction() {
            if (this.canAddApprover && this.canShowClassicApproverManagement) {
                this.openAddApprover();
                return;
            }
            this.openAddParticipant('approver');
        },
        async openReplaceParticipant(participant) {
            if (!participant || !participant.id) {
                return;
            }
            this.selectedParticipant = participant;
            this.selectedParticipantRole = participant.role;
            this.selectedParticipantUserId = null;
            this.participantOptions = [];
            this.participantActionReason = '';
            this.participantActionError = null;
            this.participantModalMode = 'replace';
            this.participantModalVisible = true;
            this.participantOptionsLoading = true;

            try {
                const result = await this.$store.dispatch(
                    'requestCard/requestCard_getParticipantReplacementOptions',
                    { participantId: participant.id }
                );
                this.participantOptions = (result && result.users) || [];
            } catch (error) {
                this.participantActionError = this.getErrorMessage(error);
            } finally {
                this.participantOptionsLoading = false;
            }
        },
        async openAddParticipant(role) {
            this.selectedParticipant = null;
            this.selectedParticipantRole = role;
            this.selectedParticipantUserId = null;
            this.participantOptions = [];
            this.participantActionReason = '';
            this.participantActionError = null;
            this.participantModalMode = 'add';
            this.participantModalVisible = true;
            this.participantOptionsLoading = true;

            try {
                const result = await this.$store.dispatch(
                    'requestCard/requestCard_getParticipantAddOptions',
                    { role }
                );
                this.participantOptions = (result && result.users) || [];
            } catch (error) {
                this.participantActionError = this.getErrorMessage(error);
            } finally {
                this.participantOptionsLoading = false;
            }
        },
        openRemoveParticipant(participant) {
            if (!participant || !participant.id) {
                return;
            }
            this.selectedParticipant = participant;
            this.selectedParticipantRole = participant.role;
            this.selectedParticipantUserId = null;
            this.participantOptions = [];
            this.participantActionReason = '';
            this.participantActionError = null;
            this.participantModalMode = 'remove';
            this.participantModalVisible = true;
        },
        async submitReplaceApprover(event) {
            if (event && event.preventDefault) {
                event.preventDefault();
            }
            if (!this.selectedApprover || !this.selectedReplacementUserId) {
                return;
            }
            if (this.isApproverReasonRequired && !this.hasApproverActionReason) {
                this.approverActionError = 'Укажите причину замены согласующего';
                return;
            }

            this.replacementLoading = true;
            this.approverActionError = null;

            try {
                await this.$store.dispatch('requestCard/requestCard_replaceApprover', {
                    participantId: this.selectedApprover.id,
                    newUserId: this.selectedReplacementUserId,
                    reason: this.approverActionReason,
                    suppressNotification: this.isCurrentUserAdmin && this.suppressApproverNotification === true,
                    godMode: this.adminGodModeEnabled === true
                });
                this.replaceModalVisible = false;
            } catch (error) {
                this.approverActionError = this.getErrorMessage(error);
            } finally {
                this.replacementLoading = false;
            }
        },
        async submitAddApprover(event) {
            if (event && event.preventDefault) {
                event.preventDefault();
            }
            if (!this.selectedAddApproverUserId) {
                return;
            }
            if (this.isApproverReasonRequired && !this.hasApproverActionReason) {
                this.approverActionError = 'Укажите причину добавления согласующего';
                return;
            }

            this.replacementLoading = true;
            this.approverActionError = null;

            try {
                await this.$store.dispatch('requestCard/requestCard_addApprover', {
                    newUserId: this.selectedAddApproverUserId,
                    reason: this.approverActionReason,
                    suppressNotification: this.isCurrentUserAdmin && this.suppressApproverNotification === true,
                    godMode: this.adminGodModeEnabled === true
                });
                this.addModalVisible = false;
            } catch (error) {
                this.approverActionError = this.getErrorMessage(error);
            } finally {
                this.replacementLoading = false;
            }
        },
        async submitRemoveApprover(event) {
            if (event && event.preventDefault) {
                event.preventDefault();
            }
            if (!this.selectedApprover) {
                return;
            }
            if (this.isApproverReasonRequired && !this.hasApproverActionReason) {
                this.approverActionError = 'Укажите причину удаления согласующего';
                return;
            }

            this.replacementLoading = true;
            this.approverActionError = null;

            try {
                await this.$store.dispatch('requestCard/requestCard_removeApprover', {
                    participantId: this.selectedApprover.id,
                    reason: this.approverActionReason,
                    suppressNotification: this.isCurrentUserAdmin && this.suppressApproverNotification === true,
                    godMode: this.adminGodModeEnabled === true
                });
                this.removeModalVisible = false;
            } catch (error) {
                this.approverActionError = this.getErrorMessage(error);
            } finally {
                this.replacementLoading = false;
            }
        },
        async submitParticipantAction(event) {
            if (event && event.preventDefault) {
                event.preventDefault();
            }

            if (!this.participantModalMode) {
                return;
            }
            if (this.participantModalMode !== 'add' && !this.selectedParticipant) {
                return;
            }
            if (this.participantModalMode !== 'remove' && !this.selectedParticipantUserId) {
                return;
            }
            if (this.isParticipantReasonRequired && !this.hasParticipantActionReason) {
                this.participantActionError = 'Укажите причину изменения участника';
                return;
            }

            this.participantActionLoading = true;
            this.participantActionError = null;

            try {
                if (this.participantModalMode === 'replace') {
                    await this.$store.dispatch('requestCard/requestCard_replaceParticipant', {
                        participantId: this.selectedParticipant.id,
                        newUserId: this.selectedParticipantUserId,
                        reason: this.participantActionReason
                    });
                } else if (this.participantModalMode === 'add') {
                    await this.$store.dispatch('requestCard/requestCard_addParticipant', {
                        role: this.selectedParticipantRole,
                        newUserId: this.selectedParticipantUserId,
                        reason: this.participantActionReason
                    });
                } else if (this.participantModalMode === 'remove') {
                    await this.$store.dispatch('requestCard/requestCard_removeParticipant', {
                        participantId: this.selectedParticipant.id,
                        reason: this.participantActionReason
                    });
                }
                this.participantModalVisible = false;
            } catch (error) {
                this.participantActionError = this.getErrorMessage(error);
            } finally {
                this.participantActionLoading = false;
            }
        },
        resetApproverModalState() {
            if (this.replacementLoading) {
                return;
            }
            this.selectedApprover = null;
            this.selectedReplacementUserId = null;
            this.selectedAddApproverUserId = null;
            this.replacementOptions = [];
            this.addOptions = [];
            this.replacementOptionsLoading = false;
            this.addOptionsLoading = false;
            this.approverActionReason = '';
            this.approverActionError = null;
            this.suppressApproverNotification = false;
        },
        resetParticipantModalState() {
            if (this.participantActionLoading) {
                return;
            }
            this.participantModalMode = null;
            this.selectedParticipant = null;
            this.selectedParticipantRole = null;
            this.selectedParticipantUserId = null;
            this.participantOptions = [];
            this.participantOptionsLoading = false;
            this.participantActionReason = '';
            this.participantActionError = null;
        },
        getErrorMessage(error) {
            return (error && error.response && error.response.data && error.response.data.message)
                || (error && error.message)
                || 'Операция не выполнена';
        }
    }
};
</script>

<style scoped>
.participants-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem 1rem;
}

.participant-column {
    flex: 1 1 100%;
    min-width: 0;
}

.participant-section {
    min-width: 0;
    margin-bottom: 0;
}

.participant-role-title {
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.4rem;
    font-size: 0.875rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.participant-role-heading {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
  margin-bottom: 0.4rem;
}

.participant-role-heading .participant-role-title {
  flex: 1 1 auto;
  min-width: 0;
  margin-bottom: 0;
}

.participant-item {
    display: grid;
    grid-template-columns: max-content minmax(0, 1fr) auto;
    column-gap: 0.45rem;
    row-gap: 0.25rem;
    align-items: center;
    min-width: 0;
    padding: 0.2rem 0;
    font-size: 0.85rem;
    line-height: 1.35;
}

.participant-empty {
  color: #6c757d;
  font-size: 0.8rem;
  line-height: 1.35;
}

.participant-status {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: auto;
    max-width: 100%;
    min-height: 1.15rem;
    padding-left: 0.3rem;
    padding-right: 0.3rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.15;
}

.observer-badge {
  color: #495057;
  background-color: #eef2f6;
}

.participant-name {
    min-width: 0;
    max-width: 100%;
    font-weight: 500;
    word-break: normal;
    overflow-wrap: normal;
}

.participant-name >>> .employee-link-wrap,
.participant-name >>> .employee-link {
    max-width: 100%;
    min-width: 0;
}

.participant-name >>> .employee-link__name {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.25;
    word-break: normal;
    overflow-wrap: normal;
}

.participant-actions {
    grid-column: 3;
    grid-row: 1;
    justify-self: end;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: nowrap;
    gap: 0.25rem;
}

.participant-action-btn {
  padding: 0.15rem 0.45rem;
  font-size: 0.75rem;
  line-height: 1.25;
}

.participant-actions .participant-action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 1.75rem;
    height: 1.75rem;
    padding: 0;
    font-size: 0;
    line-height: 1;
}

.participant-actions .participant-action-btn i {
    margin: 0;
    font-size: 0.78rem;
    line-height: 1;
}

@media (min-width: 768px) {
    .participant-column {
        flex: 0 1 270px;
        min-width: min-content;
    }

    .participant-role-title {
        font-size: 0.8rem;
        margin-bottom: 0.3rem;
    }

    .participant-item {
        grid-template-columns: max-content max-content auto;
        justify-content: start;
        font-size: 0.8rem;
    }
}
</style>
